// src/components/Control/Operator/Recordings.tsx
// Запись с камеры 1 (нода recorder на Pi): настройки и просмотр любой части записи.
//   robot/<id>/video/cmd    <- {"cmd":"config", ...VideoConfig} | {"cmd":"delete","from","to"} | {"cmd":"delete_all"}
//   robot/<id>/video/state  -> VideoState (telemetry)
//   robot/<id>/video/index  (get) -> {"spans": [[start_ms, end_ms], ...], "now"}
//   robot/<id>/video/frames/<from_ms>/<span_s>/<min_gap_ms>  (get) -> кадры подряд:
//       uint64 LE время мс, uint32 LE длина, JPEG
// На роботе запись лежит кусками по 3 минуты, но плеер просит кадры по времени,
// а не по файлам: куски идут одним потоком, промежутки без движения проскакиваются.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { useRobot, useTelemetryState } from '../robot/RobotContext';
import { STALE, VideoConfig, VideoState, fresh } from '../robot/telemetry';
import { getBytes as getRaw, putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { Segmented } from './ui';

const STATUS_RU: Record<string, [string, string]> = {
  off: ['выключена', 'off'], recording: ['идёт запись', 'alert'], waiting: ['ждёт движения', 'ok'],
  no_camera: ['нет кадров с камеры', 'warn'], asleep: ['робот спит', 'off'], disk_full: ['мало места на диске', 'alert'],
};
const SPEEDS = [1, 2, 4, 8, 16];
const DAY = 86_400_000;

const gb = (b: number | null | undefined) => (b == null ? '—' : b >= 1e9 ? `${(b / 1e9).toFixed(1)} ГБ` : `${Math.round(b / 1e6)} МБ`);
const two = (n: number) => String(n).padStart(2, '0');
const clock = (t: number) => {
  const d = new Date(t);
  return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
};
const dayStart = (t: number) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const dayName = (t: number) => new Date(t).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });

const getBytes = (session: zenoh.Session, key: string) => getRaw(session, key, 'робот не ответил (нода recorder не запущена?)');

function unpackFrames(bytes: Uint8Array): { t: number; data: Uint8Array }[] {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out: { t: number; data: Uint8Array }[] = [];
  let pos = 0;
  while (pos + 12 <= bytes.length) {
    const t = dv.getUint32(pos, true) + dv.getUint32(pos + 4, true) * 2 ** 32;
    const n = dv.getUint32(pos + 8, true);
    pos += 12;
    out.push({ t, data: bytes.subarray(pos, pos + n) });
    pos += n;
  }
  return out;
}

function useVideoState(): VideoState | null {
  useTick(2000);
  const { video, at } = useTelemetryState(t => ({ video: t.video, at: t.videoAt }));
  return video && fresh(at, STALE.node) ? video : null;
}

// --- настройки ------------------------------------------------------------------------

const NumField: React.FC<{ label: string; value: number; min: number; max: number; step?: number; suffix?: string; onCommit: (v: number) => void }> = ({ label, value, min, max, step = 1, suffix, onCommit }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const v = Number(draft.replace(',', '.'));
    if (Number.isFinite(v)) onCommit(Math.min(max, Math.max(min, v)));
    setDraft(null);
  };
  return (
    <label className="op-num">
      <span>{label}</span>
      <span className="op-num-input">
        <input type="number" value={draft ?? value} min={min} max={max} step={step}
               onChange={e => setDraft(e.target.value)} onBlur={commit}
               onKeyDown={e => { if (e.key === 'Enter') commit(); }} />
        {suffix && <small>{suffix}</small>}
      </span>
    </label>
  );
};

export const RecordingSettings: React.FC = () => {
  const { session, robotId } = useRobot();
  const st = useVideoState();
  const cfg = (update: Partial<VideoConfig>) => putJson(session, `robot/${robotId}/video/cmd`, { cmd: 'config', ...update });
  const [label, cls] = st ? STATUS_RU[st.status] ?? [st.status, 'off'] : ['нет данных', 'off'];
  const c = st?.config;
  return (
    <div className="feature-card">
      <h3><span>Запись с камеры 1</span><span className={`badge ${cls}`}>{label}</span></h3>
      {!st && <p className="hint">Нода recorder на роботе не отвечает (не запущена или старая версия ПО на Pi).</p>}
      {st?.error && <p className="error-text">{st.error}</p>}
      {c && (
        <>
          <label className="op-check">
            <input type="checkbox" checked={c.enabled} onChange={e => cfg({ enabled: e.target.checked })} />
            Записывать
          </label>
          <div className="row">
            <Segmented<VideoConfig['mode']> label="Режим записи" value={c.mode} onChange={mode => cfg({ mode })}
                                            options={[['continuous', 'Постоянно'], ['motion', 'Только при движении']]} />
          </div>
          <div className="op-num-grid">
            <NumField label="Кадров в секунду" value={c.fps} min={0.5} max={15} step={0.5} onCommit={v => cfg({ fps: v })} />
            <NumField label="Хранить не больше" value={c.max_gb} min={0.5} max={1000} step={0.5} suffix="ГБ" onCommit={v => cfg({ max_gb: v })} />
            <NumField label="Хранить дней (0 — без срока)" value={c.max_days} min={0} max={365} onCommit={v => cfg({ max_days: Math.round(v) })} />
          </div>
          {c.mode === 'motion' && (
            <>
              <div className="row">
                <label htmlFor="rec-sens">Чувствительность</label>
                <input id="rec-sens" type="range" min={1} max={10} step={1} value={c.sensitivity}
                       onChange={e => cfg({ sensitivity: Number(e.target.value) })} />
                <span className="hint">{c.sensitivity} из 10 · движение в кадре сейчас: {(st!.motion * 100).toFixed(1)}%</span>
              </div>
              <div className="op-num-grid">
                <NumField label="Писать до движения" value={c.pre_s} min={0} max={30} suffix="с" onCommit={v => cfg({ pre_s: v })} />
                <NumField label="И после него" value={c.post_s} min={2} max={300} suffix="с" onCommit={v => cfg({ post_s: v })} />
              </div>
            </>
          )}
          <p className="hint">
            Занято {gb(st!.used_bytes)}, свободно на диске {gb(st!.free_bytes)}
            {st!.oldest ? ` · записи с ${new Date(st!.oldest).toLocaleString('ru-RU')}` : ' · записей нет'}
            {st!.status === 'recording' ? ` · ${st!.fps.toFixed(1)} к/с` : ''}.
            Старые записи удаляются сами, когда превышен объём или срок. Кадры пишутся без пережатия
            (~1–2 ГБ в час при 5 к/с). Пока запись включена, робот не засыпает сам («Питание и сон»);
            во сне камера выключена и запись стоит.
          </p>
        </>
      )}
    </div>
  );
};

// --- просмотр -------------------------------------------------------------------------

interface Frame { t: number; url: string }

function usePlayback(session: zenoh.Session | null, prefix: string) {
  const [shown, setShown] = useState<Frame | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(1);
  const [note, setNoteState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Подсказка поверх кадра («Пропуск…») гаснет сама; «Конец записи» — нет
  const setNote = useCallback((text: string | null, ms = 4000) => {
    if (noteTimer.current) clearTimeout(noteTimer.current);
    setNoteState(text);
    if (text && ms > 0) noteTimer.current = setTimeout(() => setNoteState(null), ms);
  }, []);
  const s = useRef({ buf: [] as Frame[], media: 0, fetching: false, end: false, gen: 0, playing: false, speed: 1,
                     shown: null as Frame | null, endAt: 0 });

  const show = (f: Frame) => {
    const prev = s.current.shown;
    s.current.shown = f;
    setShown(f);
    if (prev && prev !== f) setTimeout(() => URL.revokeObjectURL(prev.url), 1000);
  };

  const fetchFrom = useCallback((from: number) => {
    const st = s.current;
    if (!session || st.fetching) return;
    st.fetching = true;
    const gen = st.gen;
    const span = Math.min(60, Math.max(4, 4 * st.speed));
    const gap = st.speed <= 1 ? 0 : Math.round((1000 * st.speed) / 10);  // не больше ~10 кадров/с на экране
    setLoading(st.buf.length === 0);
    getBytes(session, `${prefix}/video/frames/${Math.floor(from)}/${span}/${gap}`)
      .then(bytes => {
        if (gen !== st.gen) return;
        const frames = unpackFrames(bytes);
        setError(null);
        if (!frames.length) {
          st.end = true;
          st.endAt = Date.now();
          return;
        }
        const last = st.buf.length ? st.buf[st.buf.length - 1].t : -1;
        for (const f of frames) {
          if (f.t > last) st.buf.push({ t: f.t, url: URL.createObjectURL(new Blob([f.data as BlobPart], { type: 'image/jpeg' })) });
        }
        // На паузе после перехода — сразу показать первый кадр
        if (!st.playing && st.buf.length && (!st.shown || st.media <= st.buf[0].t)) {
          st.media = Math.max(st.media, st.buf[0].t);
          show(st.buf[0]);
        }
      })
      .catch(e => { if (gen === st.gen) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => {
        if (gen === st.gen) {
          st.fetching = false;
          setLoading(false);
        }
      });
  }, [session, prefix]);

  const seek = useCallback((t: number) => {
    const st = s.current;
    st.gen++;
    st.fetching = false;
    st.buf.forEach(f => { if (f !== st.shown) URL.revokeObjectURL(f.url); });
    st.buf = [];
    st.media = t;
    st.end = false;
    setNote(null);
    fetchFrom(t);
  }, [fetchFrom, setNote]);

  const setPlayingBoth = (p: boolean) => {
    s.current.playing = p;
    setPlaying(p);
  };
  const setSpeed = (v: number) => {
    s.current.speed = v;
    setSpeedState(v);
  };

  // Ход воспроизведения
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = setInterval(() => {
      const st = s.current;
      const now = performance.now();
      st.media += (now - last) * st.speed;
      last = now;
      let i = -1;
      while (i + 1 < st.buf.length && st.buf[i + 1].t <= st.media) i++;
      // Промежуток без записи (движения не было) — сразу к следующему кадру. На быстрой
      // скорости кадры прорежены, поэтому порог растёт со скоростью.
      const next = st.buf[i + 1];
      if (next && next.t > st.media + 1500 + 300 * st.speed) {
        const from = i >= 0 ? st.buf[i].t : st.media;
        if (next.t - from > 5000) setNote(`Пропуск: записи нет ${clock(from)} → ${clock(next.t)}`);
        st.media = next.t;
        i++;
      }
      if (i >= 0) {
        const f = st.buf[i];
        st.buf.splice(0, i).forEach(old => { if (old !== st.shown) URL.revokeObjectURL(old.url); });
        if (f !== st.shown) show(f);
      }
      const lastT = st.buf.length ? st.buf[st.buf.length - 1].t : st.media;
      if (!st.fetching && !st.end && lastT - st.media < 3000 * st.speed) fetchFrom(lastT + 1);
      if (st.end && st.buf.length <= 1 && st.media > lastT + 500) {
        if (Date.now() - st.endAt > 1000) {  // запись ещё идёт — догоняем, что появилось
          st.end = false;
          if (st.media > lastT + 15000) {
            setPlayingBoth(false);
            setNote('Конец записи', 0);
          }
        }
        st.media = Math.min(st.media, lastT + 15000);
      }
    }, 50);
    return () => clearInterval(id);
  }, [playing, fetchFrom, setNote]);

  useEffect(() => () => {
    if (noteTimer.current) clearTimeout(noteTimer.current);
    const st = s.current;
    st.gen++;
    st.buf.forEach(f => URL.revokeObjectURL(f.url));
    if (st.shown) URL.revokeObjectURL(st.shown.url);
  }, []);

  return { shown, playing, setPlaying: setPlayingBoth, speed, setSpeed, seek, note, error, loading,
           position: () => (s.current.shown ? s.current.shown.t : s.current.media) };
}

// Полоса времени: отрезки записи, ползунок, клик — переход
const Timeline: React.FC<{ from: number; to: number; spans: number[][]; at: number | null; ticks: number; tickLabel: (t: number) => string; onSeek: (t: number) => void; label: string }> = ({ from, to, spans, at, ticks, tickLabel, onSeek, label }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const tOf = (clientX: number) => {
    const r = ref.current!.getBoundingClientRect();
    return from + Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * (to - from);
  };
  const pct = (t: number) => `${((t - from) / (to - from)) * 100}%`;
  const visible = spans.filter(([a, b]) => b >= from && a <= to);
  return (
    <div className="op-rec-timeline">
      <div ref={ref} className="bar" role="slider" aria-label={label} tabIndex={0}
           aria-valuemin={from} aria-valuemax={to} aria-valuenow={at ?? from} aria-valuetext={at ? clock(at) : 'нет'}
           onClick={e => onSeek(tOf(e.clientX))}
           onPointerMove={e => setHover(tOf(e.clientX))} onPointerLeave={() => setHover(null)}
           onKeyDown={e => {
             const step = (to - from) / 100;
             if (e.key === 'ArrowRight' && at) onSeek(at + step);
             if (e.key === 'ArrowLeft' && at) onSeek(at - step);
           }}>
        {visible.map(([a, b]) => (
          <div key={a} className="span" style={{ left: pct(Math.max(a, from)), width: `max(2px, ${((Math.min(b, to) - Math.max(a, from)) / (to - from)) * 100}%)` }} />
        ))}
        {at !== null && at >= from && at <= to && <div className="head" style={{ left: pct(at) }} />}
        {hover !== null && <div className="hover" style={{ left: pct(hover) }}><span>{clock(hover)}</span></div>}
      </div>
      <div className="ticks" aria-hidden="true">
        {Array.from({ length: ticks + 1 }, (_, k) => <span key={k}>{tickLabel(from + (k * (to - from)) / ticks)}</span>)}
      </div>
    </div>
  );
};

export const RecordingPlayer: React.FC = () => {
  const { session, robotId, notify } = useRobot();
  const prefix = `robot/${robotId}`;
  const st = useVideoState();
  const [spans, setSpans] = useState<number[][]>([]);
  const [indexError, setIndexError] = useState<string | null>(null);
  const [day, setDay] = useState<number | null>(null);
  const p = usePlayback(session, prefix);
  useTick(p.playing ? 500 : 2000);

  const loadIndex = useCallback(() => {
    if (!session) return;
    getBytes(session, `${prefix}/video/index`)
      .then(b => {
        const msg = JSON.parse(new TextDecoder().decode(b)) as { spans: number[][] };
        setSpans(msg.spans ?? []);
        setIndexError(null);
      })
      .catch(e => setIndexError(e instanceof Error ? e.message : String(e)));
  }, [session, prefix]);
  useEffect(() => {
    loadIndex();
    const id = setInterval(loadIndex, 15000);
    return () => clearInterval(id);
  }, [loadIndex]);

  const days = useMemo(() => {
    const set = new Set<number>();
    spans.forEach(([a, b]) => { for (let d = dayStart(a); d <= b; d += DAY) set.add(dayStart(d + DAY / 2)); });
    return Array.from(set).sort((a, b) => b - a);
  }, [spans]);
  const curDay = day ?? days[0] ?? dayStart(Date.now());
  const at = p.shown?.t ?? null;
  const hourFrom = at !== null ? Math.floor(at / 3_600_000) * 3_600_000 : null;

  const newest = spans.length ? spans[spans.length - 1][1] : null;
  const seek = (t: number) => {
    if (dayStart(t) !== curDay) setDay(dayStart(t));
    p.seek(t);
  };
  const jump = (dt: number) => seek((at ?? curDay) + dt);
  const deleteDay = () => {
    if (!window.confirm(`Удалить записи за ${dayName(curDay)}? Отменить нельзя.`)) return;
    putJson(session, `${prefix}/video/cmd`, { cmd: 'delete', from: curDay, to: curDay + DAY - 1 })
      .then(ok => { notify(ok ? 'Записи за день удаляются' : 'Не удалось отправить команду', ok ? 'info' : 'error'); setTimeout(loadIndex, 1500); });
  };

  return (
    <div className="feature-card op-rec-player">
      <h3>
        <span>Просмотр записей</span>
        <span className="op-row">
          <select aria-label="День" value={curDay} onChange={e => setDay(Number(e.target.value))}>
            {(days.length ? days : [curDay]).map(d => <option key={d} value={d}>{dayName(d)}</option>)}
          </select>
          <button type="button" className="op-btn" onClick={loadIndex}>Обновить</button>
        </span>
      </h3>
      {indexError && <p className="error-text">{indexError}</p>}
      <div className="op-rec-screen">
        {p.shown ? <img src={p.shown.url} alt={`Кадр записи ${clock(p.shown.t)}`} /> : null}
        <span className="op-rec-time">{p.shown ? new Date(p.shown.t).toLocaleString('ru-RU') : ''}</span>
        {!p.shown && (
          <div className="op-cam-msg">
            {p.loading ? 'Загрузка…' : spans.length ? 'Выберите место на шкале времени' : st?.config.enabled ? 'Записей пока нет' : 'Записей нет — включите запись выше'}
          </div>
        )}
        {p.note && <div className="op-rec-note" role="status">{p.note}</div>}
      </div>
      {p.error && <p className="error-text">{p.error}</p>}
      <Timeline label="Время за день" from={curDay} to={curDay + DAY} spans={spans} at={at} ticks={8}
                tickLabel={t => `${two(new Date(t).getHours())}:00`} onSeek={seek} />
      {hourFrom !== null && (
        <Timeline label="Время в пределах часа" from={hourFrom} to={hourFrom + 3_600_000} spans={spans} at={at} ticks={6}
                  tickLabel={t => clock(t).slice(0, 5)} onSeek={seek} />
      )}
      <div className="op-rec-controls">
        <button type="button" className="op-btn" onClick={() => jump(-60_000)} disabled={at === null} aria-label="Назад на минуту">−1 мин</button>
        <button type="button" className="op-btn" onClick={() => jump(-10_000)} disabled={at === null} aria-label="Назад на 10 секунд">−10 с</button>
        <button type="button" className="op-btn primary" aria-label={p.playing ? 'Пауза' : 'Воспроизвести'}
                disabled={!spans.length}
                onClick={() => {
                  if (!p.playing && at === null) p.seek(spans.find(([, b]) => b >= curDay)?.[0] ?? spans[0][0]);
                  p.setPlaying(!p.playing);
                }}>
          {p.playing ? '❚❚ Пауза' : '▶ Смотреть'}
        </button>
        <button type="button" className="op-btn" onClick={() => jump(10_000)} disabled={at === null} aria-label="Вперёд на 10 секунд">+10 с</button>
        <button type="button" className="op-btn" onClick={() => jump(60_000)} disabled={at === null} aria-label="Вперёд на минуту">+1 мин</button>
        <Segmented<string> label="Скорость" value={String(p.speed)} onChange={v => p.setSpeed(Number(v))}
                           options={SPEEDS.map(v => [String(v), `${v}×`] as [string, string])} />
        <button type="button" className="op-btn" disabled={newest === null}
                onClick={() => { if (newest !== null) { seek(newest - 30_000); p.setPlaying(true); } }}>К последним</button>
        {p.shown && (
          <a className="op-btn" href={p.shown.url} download={`camera1-${new Date(p.shown.t).toISOString().replace(/[:.]/g, '-')}.jpg`}>
            Сохранить кадр
          </a>
        )}
      </div>
      <details>
        <summary>Удаление</summary>
        <div className="op-row">
          <button type="button" className="op-btn danger" onClick={deleteDay} disabled={!days.includes(curDay)}>Удалить записи за {dayName(curDay)}</button>
          <button type="button" className="op-btn danger"
                  onClick={() => {
                    if (!window.confirm('Удалить все записи с камеры? Отменить нельзя.')) return;
                    putJson(session, `${prefix}/video/cmd`, { cmd: 'delete_all' }).then(() => setTimeout(loadIndex, 1500));
                  }}>
            Удалить все записи
          </button>
        </div>
      </details>
    </div>
  );
};
